import { Controller } from "@hotwired/stimulus"

import { formatClock } from "@/lib/utils"

// The unfurl player's single transport (#1643, epic #1099). One media element
// plays the whole container; tapping a "What's inside" row swaps in a fresh
// transport for that track — no page load, no player-per-row. Mirrors the
// public container page's container-player/track-row split, collapsed onto one
// shared transport.
export interface Track {
  itemId: string
  title: string
  playbackId: string
  playbackToken: string
  src: string
  artwork: string
  duration: number
  // Set by the player when an access answer no longer carries the track: its
  // row stays where the listener saw it, but plays nothing.
  locked?: boolean
}

const ROW_SELECTOR = "[data-embed-player-target~='row']"
// HTMLMediaElement.HAVE_FUTURE_DATA: the first readiness at which play() starts.
const HAVE_FUTURE_DATA = 3

export default class extends Controller {
  static targets = [
    "nowPlaying",
    "playButton",
    "pauseButton",
    "skeleton",
    "row",
    "rowGlyph",
    "labelled",
    "rowRing",
    "transportArtwork",
    "transportArtworkPlaceholder",
    "autoPlayToggle",
    "elapsed",
    "duration",
    "progressBar",
    "seek",
    "muxTemplate",
    "audioTemplate",
  ]
  static values = {
    tracks: { type: Array, default: [] },
    index: { type: Number, default: 0 },
    autoPlay: { type: Boolean, default: false },
  }

  declare tracksValue: Track[]
  declare indexValue: number
  declare autoPlayValue: boolean
  declare readonly nowPlayingTarget: HTMLElement
  declare readonly hasNowPlayingTarget: boolean
  // Every play control on the card — the transport's button and the hero's
  // "Begin listening" pill — so one state drives them all.
  declare readonly playButtonTargets: HTMLElement[]
  declare readonly pauseButtonTargets: HTMLElement[]
  declare readonly skeletonTarget: HTMLElement
  declare readonly hasSkeletonTarget: boolean
  declare readonly rowTargets: HTMLElement[]
  declare readonly rowGlyphTargets: HTMLElement[]
  // A transport button whose name says whether it plays or is locked.
  declare readonly labelledTargets: HTMLElement[]
  declare readonly rowRingTargets: HTMLElement[]
  declare readonly transportArtworkTarget: HTMLImageElement
  declare readonly hasTransportArtworkTarget: boolean
  declare readonly transportArtworkPlaceholderTarget: HTMLElement
  declare readonly hasTransportArtworkPlaceholderTarget: boolean
  declare readonly autoPlayToggleTarget: HTMLElement
  declare readonly hasAutoPlayToggleTarget: boolean
  declare readonly elapsedTarget: HTMLElement
  declare readonly hasElapsedTarget: boolean
  declare readonly durationTarget: HTMLElement
  declare readonly hasDurationTarget: boolean
  declare readonly progressBarTarget: HTMLElement
  declare readonly hasProgressBarTarget: boolean
  declare readonly seekTarget: HTMLInputElement
  declare readonly hasSeekTarget: boolean
  declare readonly muxTemplateTarget: HTMLTemplateElement
  declare readonly hasMuxTemplateTarget: boolean
  declare readonly audioTemplateTarget: HTMLTemplateElement
  declare readonly hasAudioTemplateTarget: boolean

  // Optimistic play state — flipped on the click gesture so the UI responds
  // instantly, then kept honest by the media element's own events. A #field
  // rather than a Stimulus value: this is internal transport state derived
  // from the media element, not configuration, and a value would survive in
  // the DOM across a Turbo morph and could come back stale.
  #playing = false
  // Whether the transport we promised playback on can't play yet. Same
  // lifetime and same reasoning as #playing; re-derived on every play so it
  // never outlives the transport it describes.
  #loading = false
  // True from the first move of the handle until the listener lets go, so
  // playback's own clock cannot drag the bar out from under them mid-gesture.
  #scrubbing = false
  private listeners!: AbortController

  private get playing() {
    return this.#playing
  }

  // Assigning routes the play/pause button swap through automatically — no
  // separate reflection step for call sites to remember.
  private set playing(playing: boolean) {
    this.#playing = playing
    this.playButtonTargets.forEach((el) =>
      el.classList.toggle("hidden", playing),
    )
    this.pauseButtonTargets.forEach((el) =>
      el.classList.toggle("hidden", !playing),
    )
    this.reflectRowState()
  }

  private get loading() {
    return this.#loading
  }

  private set loading(loading: boolean) {
    this.#loading = loading
    this.reflectRowState()
  }

  connect() {
    this.listeners = new AbortController()
    this.wireMedia()
    this.reflectActiveRow()
    this.reflectLocks()
  }

  tracksValueChanged() {
    this.reflectLocks()
  }

  indexValueChanged() {
    this.reflectLocks()
  }

  disconnect() {
    this.listeners.abort()
  }

  // The transport is the media inside the player chrome. The hero's welcome
  // greeting is an <audio> of its own, mounted ahead of the transport, and
  // must never be the element "Begin listening" or a row drives.
  private get media(): (HTMLMediaElement & HTMLElement) | null {
    return this.trackingHost?.querySelector("mux-audio, audio") ?? null
  }

  // The player-chrome host (`shared/_audio_player`'s <media-controller>) — the
  // element the analytics and Media Session controllers are mounted on. With
  // one shared transport its values are a pointer at the *current* track, not
  // a fixture, so they travel with the selection.
  private get trackingHost(): HTMLElement | null {
    return this.element.querySelector("[data-controller~='play-tracker']")
  }

  private wireMedia() {
    const media = this.media
    if (!media) return
    const { signal } = this.listeners
    media.addEventListener("play", () => (this.playing = true), { signal })
    media.addEventListener("canplay", () => (this.loading = false), { signal })
    media.addEventListener("pause", () => (this.playing = false), { signal })
    media.addEventListener("ended", () => this.onEnded(), { signal })
    media.addEventListener("timeupdate", () => this.updateProgress(media), {
      signal,
    })
    const ready = () => {
      this.hideSkeleton()
      this.updateProgress(media)
    }
    media.addEventListener("loadedmetadata", ready, { signal })
    media.addEventListener("canplay", ready, { signal })
    // A browser that measured the track before this controller booted fires
    // neither event again, and the card would sit there loading with a bar
    // spanning nothing — nowhere for a listener to seek to (#2070).
    if (media.readyState >= HTMLMediaElement.HAVE_METADATA) ready()
  }

  private updateProgress(media: HTMLMediaElement) {
    const duration = media.duration
    if (duration && isFinite(duration)) {
      if (this.hasDurationTarget)
        this.durationTarget.textContent = formatClock(duration)
      // The browser's measurement beats the server's: an item whose length was
      // never measured has nowhere to seek to until this lands.
      if (this.hasSeekTarget) this.seekTarget.max = String(duration)
    }
    if (this.#scrubbing) return
    if (this.hasSeekTarget) this.seekTarget.value = String(media.currentTime)
    this.reflectPosition(media.currentTime, duration)
  }

  // Elapsed time, the bar and the active row's ring all say the same thing,
  // whether the position came from playback or from the listener's own hand.
  private reflectPosition(seconds: number, duration: number) {
    const clock = formatClock(seconds)
    if (this.hasElapsedTarget) this.elapsedTarget.textContent = clock
    // Rewritten only when the clock moves: a screen reader re-announces the
    // slider every time this changes, and playback reports four times a second.
    if (
      this.hasSeekTarget &&
      this.seekTarget.getAttribute("aria-valuetext") !== clock
    )
      this.seekTarget.setAttribute("aria-valuetext", clock)
    if (!duration || !isFinite(duration)) return
    const ratio = seconds / duration
    if (this.hasProgressBarTarget)
      this.progressBarTarget.style.width = `${ratio * 100}%`
    this.reflectRowRings(ratio)
  }

  // The handle is moving: show where it would land, but leave playback alone.
  // Seeking on every step of a drag across an hour-long talk would ask Mux for
  // a new part of the stream dozens of times over for a position the listener
  // is only passing through.
  scrub() {
    this.#scrubbing = true
    this.reflectPosition(this.handleSeconds, this.trackSeconds)
  }

  // They let go (or pressed an arrow key): move playback to the handle. Bound
  // to the pointer release as well as to change, because a drag that ends back
  // where it started fires no change in Firefox, and the bar would then stop
  // following the track for the rest of it. A normal drag reports its release
  // through both, so the track is only moved when it is not already there —
  // one release asks Mux for one position, however many times it is announced.
  //
  // The element is read now rather than held, because choosing another row
  // mounts a fresh transport and the old one is no longer what anyone is
  // listening to. Seeking stays inside the stream the transport is already
  // playing — the Mux token it was mounted with still covers it.
  seek() {
    this.#scrubbing = false
    const seconds = this.handleSeconds
    const media = this.media
    if (media && this.trackSeconds > 0 && media.currentTime !== seconds)
      media.currentTime = seconds
    this.reflectPosition(seconds, this.trackSeconds)
  }

  // A gesture can end without committing anything: Escape during a drag puts
  // the handle back, and focus can leave mid-gesture. Left alone the bar would
  // stop following the track for the rest of it. Both events land after any
  // commit, so a gesture that did seek has already cleared this.
  endScrub() {
    if (!this.#scrubbing) return
    this.#scrubbing = false
    const media = this.media
    if (media) this.updateProgress(media)
  }

  private get handleSeconds(): number {
    return this.hasSeekTarget ? Number(this.seekTarget.value) : 0
  }

  // How long the transport believes the current track is — the range's own
  // span, which is the server's number until the browser measures the real one.
  private get trackSeconds(): number {
    const seconds = this.hasSeekTarget ? Number(this.seekTarget.max) : 0
    return isFinite(seconds) ? seconds : 0
  }

  // The active row's number ring fills as the track plays, like the container
  // page's; every other ring stays empty.
  private reflectRowRings(ratio: number) {
    this.rowRingTargets.forEach((ring) => {
      const value = this.inActiveRow(ring) ? Math.round(ratio * 100) : 0
      ring.setAttribute("value", String(value))
    })
  }

  private inActiveRow(el: Element): boolean {
    const row = el.closest(ROW_SELECTOR)
    return (
      row instanceof HTMLElement &&
      Number(row.dataset.embedPlayerIndexParam) === this.indexValue
    )
  }

  // The transport shows the playing track's artwork, the placeholder when it
  // has none — the global bar's thumbnail, following the selection.
  private reflectArtwork(track: Track) {
    const artwork = track.artwork
    if (this.hasTransportArtworkTarget) {
      if (artwork) this.transportArtworkTarget.setAttribute("src", artwork)
      this.transportArtworkTarget.classList.toggle("hidden", !artwork)
    }
    if (this.hasTransportArtworkPlaceholderTarget)
      this.transportArtworkPlaceholderTarget.classList.toggle(
        "hidden",
        !!artwork,
      )
  }

  // A "What's inside" row was tapped: re-source the shared transport in place.
  // A padlock row asks the card to let the listener in instead (#2242).
  selectTrack(event: { params: { index: number } }) {
    const index = event.params.index
    if (this.tracksValue[index]?.locked) {
      this.dispatch("locked")
      return
    }
    if (index === this.indexValue) {
      this.togglePlay()
      return
    }
    this.indexValue = index
    this.loadTrack(index)
    this.play()
  }

  togglePlay() {
    if (this.tracksValue[this.indexValue]?.locked) {
      this.dispatch("locked")
      return
    }
    if (this.playing) {
      this.pause()
    } else {
      this.play()
    }
  }

  private play() {
    this.playing = true
    const media = this.media
    if (!media) return
    // Read off the transport at the tap, not off how play() answers: a
    // browser may hold the promise open while it loads rather than reject,
    // and the row must spin either way (#2051).
    this.loading = !this.canPlayNow(media)
    // Captured now, not inside the rejection: a swap replaces this controller,
    // so reading it later would tie the retry to a transport it never asked
    // about.
    const { signal } = this.listeners

    void media.play?.()?.catch?.(() => {
      // A rejection can land after the listener has already moved on to a
      // row whose transport replaced this one; it must not touch that row.
      if (this.media !== media) return
      // A transport that has already failed, or has already become playable
      // while the answer was in flight, will never fire canplay again — so
      // waiting for it would leave the row spinning forever.
      if (media.error) {
        this.givingUpOn(media)
        return
      }
      if (this.canPlayNow(media)) {
        void media.play?.()?.catch?.(() => this.givingUpOn(media))
        return
      }
      // A transport mounted a moment ago is still loading, and the browser
      // rejects play() on it outright — so the track a listener tapped never
      // starts, silently. Ask again as soon as it can play (#2030). The first
      // row escaped this because the server rendered its element and it had
      // long since loaded.
      media.addEventListener(
        "canplay",
        () => {
          // Only if they still want this track: they may have paused, or moved
          // on to a row whose transport replaced this one.
          if (!this.playing || this.media !== media) return

          void media.play?.()?.catch?.(() => this.givingUpOn(media))
        },
        { once: true, signal },
      )
      // Nothing will ever be playable, so the retry above is never reached.
      media.addEventListener("error", () => this.givingUpOn(media), {
        once: true,
        signal,
      })
    })
  }

  // The button and the active row promise playback from the moment of the tap,
  // and a play that never starts fires no pause event to take that back — so
  // the listener is left watching a pause glyph over silence (#2030).
  private givingUpOn(media: HTMLMediaElement & HTMLElement) {
    if (this.media !== media) return
    this.loading = false
    this.playing = false
  }

  private canPlayNow(media: HTMLMediaElement): boolean {
    return media.readyState >= HAVE_FUTURE_DATA
  }

  private pause() {
    this.loading = false
    this.playing = false
    this.media?.pause?.()
  }

  private loadTrack(index: number) {
    const track = this.tracksValue[index]
    if (!track) return

    // Re-point the trackers first: sourceTransport announces an element swap
    // synchronously, and the handlers read these values off the host as they
    // run. Announcing first meant they read the previous track's.
    this.retargetTracking(track)
    this.sourceTransport(track)

    if (this.hasNowPlayingTarget)
      this.nowPlayingTarget.textContent = track.title
    if (this.hasElapsedTarget) this.elapsedTarget.textContent = formatClock(0)
    if (this.hasDurationTarget)
      this.durationTarget.textContent = formatClock(track.duration)
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "0%"
    if (this.hasSeekTarget) {
      this.#scrubbing = false
      this.seekTarget.max = String(track.duration)
      this.seekTarget.value = "0"
      this.seekTarget.setAttribute("aria-valuetext", formatClock(0))
    }
    this.reflectArtwork(track)
    this.reflectActiveRow()
    this.reflectRowRings(0)
  }

  // Attribution follows the transport. The host was rendered for the first
  // item and play-tracker reports only the first play it sees per item, so
  // left alone every play in the session would be filed under track 1 —
  // wrong item id, wrong title, wrong artwork on the lock screen. Writing the
  // values back (Stimulus picks the attribute change up) re-points the
  // trackers and resets play-tracker's per-item latch.
  private retargetTracking(track: Track) {
    const host = this.trackingHost
    if (!host) return

    host.setAttribute("data-play-tracker-item-id-value", track.itemId)
    host.setAttribute("data-play-tracker-item-title-value", track.title)
    host.setAttribute("data-audio-player-item-title-value", track.title)
    host.setAttribute("data-audio-player-artwork-url-value", track.artwork)
    // Mux Data only rides along when the env key is configured.
    if (host.hasAttribute("data-mux-data-tracker-item-id-value")) {
      host.setAttribute("data-mux-data-tracker-item-id-value", track.itemId)
      host.setAttribute("data-mux-data-tracker-item-title-value", track.title)
    }
  }

  // A container can mix Mux-backed items with direct-content ones (an item
  // whose Mux ingest hasn't landed yet), and <mux-audio> ignores `src` while
  // plain <audio> ignores `playback-id`, so the element has to match the
  // track's source. It is always a fresh element, never the one that was
  // just playing: re-pointing that one hands Mux the next playback id while
  // the previous track's token is still on it, Mux refuses the pair, and the
  // listener who tapped the second track hears nothing (#2017).
  private sourceTransport(track: Track) {
    const wanted = track.playbackId
      ? "mux-audio"
      : track.src
        ? "audio"
        : // Nothing to play — leave the transport as it stands.
          null
    if (!wanted) return

    const media = this.media
    if (!media) return

    const replacement = this.buildTransport(wanted)
    if (!replacement) return

    // Token before id: the id is what starts the load, and it must find the
    // token already there.
    if (wanted === "mux-audio") {
      if (track.playbackToken) {
        replacement.setAttribute("playback-token", track.playbackToken)
      }
      replacement.setAttribute("playback-id", track.playbackId)
    } else {
      replacement.setAttribute("src", track.src)
    }

    this.swapTransport(media, replacement)
  }

  private swapTransport(
    media: HTMLMediaElement & HTMLElement,
    replacement: HTMLMediaElement & HTMLElement,
  ) {
    // A detached media element keeps sounding; silence it before it is
    // orphaned, with its listeners already gone so its own pause event
    // cannot flip the chrome the new track is about to claim.
    this.listeners.abort()
    media.pause?.()
    media.replaceWith(replacement)
    this.listeners = new AbortController()
    this.wireMedia()
    // So did the sibling controllers mounted on the host — audio-player
    // cached the element for Media Session, mux-data-tracker for QoE
    // monitoring. Announce the swap on the host so they re-resolve it
    // instead of holding an orphan (wired via data-action in
    // shared/_audio_player).
    this.dispatch("mediaswapped", { target: this.trackingHost ?? undefined })
  }

  private buildTransport(
    tag: "mux-audio" | "audio",
  ): (HTMLMediaElement & HTMLElement) | null {
    const template =
      tag === "mux-audio"
        ? this.hasMuxTemplateTarget
          ? this.muxTemplateTarget
          : null
        : this.hasAudioTemplateTarget
          ? this.audioTemplateTarget
          : null

    const built = template?.content.firstElementChild?.cloneNode(true)
    return (built as (HTMLMediaElement & HTMLElement) | undefined) ?? null
  }

  // data-action="embed-access:changed@window->embed-player#applyAccess"
  // A sign-in, a join or a sign-out changes what this listener may play
  // (#2040). A track they may still play keeps playing from where it is; one
  // they may no longer play stops, and its source leaves the page. Rows keep
  // the order the card drew them in, so every row still plays its own track.
  applyAccess(event: CustomEvent<{ state: string; tracks?: Track[] }>) {
    const allowed =
      event.detail.state === "playable" ? (event.detail.tracks ?? []) : []
    const byItem = new Map(allowed.map((track) => [track.itemId, track]))
    const rows = this.tracksValue.length > 0 ? this.tracksValue : allowed
    this.tracksValue = rows.map(
      (track) => byItem.get(track.itemId) ?? locked(track),
    )
    this.reflectLocks()

    if (!this.tracksValue[this.indexValue]?.locked) {
      if (!this.sourced) this.loadTrack(this.indexValue)
      return
    }

    this.pause()
    const next = this.tracksValue.findIndex((track) => !track.locked)
    if (next >= 0) {
      this.indexValue = next
      this.loadTrack(next)
    } else {
      this.unloadTransport()
    }
  }

  private get sourced(): boolean {
    const media = this.media
    return (
      !!media &&
      (media.hasAttribute("playback-id") || !!media.getAttribute("src"))
    )
  }

  private unloadTransport() {
    const media = this.media
    if (!media) return
    const empty = this.buildTransport(
      media.tagName.toLowerCase() === "mux-audio" ? "mux-audio" : "audio",
    )
    if (!empty) return

    this.swapTransport(media, empty)
    if (this.hasElapsedTarget) this.elapsedTarget.textContent = formatClock(0)
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "0%"
    if (this.hasSeekTarget) this.seekTarget.value = "0"
    this.reflectRowRings(0)
  }

  toggleAutoPlay() {
    this.autoPlayValue = !this.autoPlayValue
  }

  autoPlayValueChanged(value: boolean) {
    if (!this.hasAutoPlayToggleTarget) return
    this.autoPlayToggleTarget.setAttribute("aria-pressed", String(value))
  }

  private onEnded() {
    this.playing = false
    if (!this.autoPlayValue) return
    const next = this.indexValue + 1
    if (next < this.tracksValue.length && !this.tracksValue[next].locked) {
      this.indexValue = next
      this.loadTrack(next)
      this.play()
    }
  }

  private reflectActiveRow() {
    this.rowTargets.forEach((row) => {
      const active = this.inActiveRow(row)
      row.setAttribute("aria-current", String(active))
      row.classList.toggle("bg-violet-tint", active)
    })
    this.reflectRowState()
  }

  // The active row is the transport's twin: pause exactly while the transport
  // plays, busy (the view swaps in a spinner) while the track it promised is
  // still loading (#2051), play everywhere else. No row is ever disabled — a
  // listener changing their mind mid-load must be able to.
  private reflectRowState() {
    const waiting = this.playing && this.loading
    this.rowTargets.forEach((row) => {
      row.setAttribute("aria-busy", String(waiting && this.inActiveRow(row)))
    })
    this.rowGlyphTargets.forEach((glyph) => {
      const active = this.inActiveRow(glyph)
      glyph.setAttribute("name", active && this.playing ? "pause" : "play")
    })
  }

  // A locked row or transport must say so, to the eye and to a screen
  // reader, and say so no longer once the listener may play (#2242).
  private reflectLocks() {
    const current = !!this.tracksValue[this.indexValue]?.locked
    this.element.toggleAttribute("data-locked", current)
    this.labelledTargets.forEach((button) => {
      const label = current
        ? button.dataset.lockedLabel
        : button.dataset.unlockedLabel
      if (label) button.setAttribute("aria-label", label)
    })
    this.rowTargets.forEach((row) => {
      const index = Number(row.dataset.embedPlayerIndexParam)
      const locked = !!this.tracksValue[index]?.locked
      row.toggleAttribute("data-locked", locked)
      const label = row.dataset.lockedLabel
      if (locked && label) row.setAttribute("aria-label", label)
      else row.removeAttribute("aria-label")
    })
  }

  private hideSkeleton() {
    if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden")
  }
}

function locked(track: Track): Track {
  return { ...track, playbackId: "", playbackToken: "", src: "", locked: true }
}
